import * as Sentry from "@sentry/react";
import { DirtyFormProvider } from "@simplicity/ui";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { BrowserRouter, NavLink, Route, Routes, useLocation, useNavigate } from "react-router";
import { ConnectorDetails } from "./components/config/ConnectorDetails";
import { CreateConnector } from "./components/config/CreateConnector";
import { CreateDestination } from "./components/config/CreateDestination";
import { CreateExportDefinition } from "./components/config/CreateExportDefinition";
import { CreateProject } from "./components/config/CreateProject";
import { CreateTransformDefinition } from "./components/config/CreateTransformDefinition";
import { DestinationDetails } from "./components/config/DestinationDetails";
import { ExportDefinitionDetails } from "./components/config/ExportDefinitionDetails";
import { FeatureFlagDetail } from "./components/config/FeatureFlagDetail";
import { FeatureFlagsList } from "./components/config/FeatureFlagsList";
import { GenerateApiKey } from "./components/config/GenerateApiKey";
import { ProjectDetails } from "./components/config/ProjectDetails";
import { ProjectsList } from "./components/config/ProjectsList";
import { TransformDefinitionDetails } from "./components/config/TransformDefinitionDetails";
import { DocumentTitleEffect, type RouteTitle } from "./components/DocumentTitleEffect";
import { Login } from "./components/Login";
import { PipelinePage } from "./components/pipeline/PipelinePage";
import { ReleaseWatcher } from "./components/ReleaseWatcher";
import type { RunsSummaryResponse } from "./components/RunsReport";
import { RunsReport } from "./components/RunsReport";
import { RunsTable } from "./components/RunsTable";
import { Timeline } from "./components/Timeline";
import { DottieContextPage } from "./pages/dottieContext/DottieContextPage";
import { OnsitePage } from "./pages/onsite/OnsitePage";
import type { RunsReportResponse, TimelineRunsReportResponse } from "./types";

const SentryRoutes = Sentry.withSentryReactRouterV7Routing(Routes);

const TITLE_SUFFIX = " | Simplicity Admin";
const TITLE_FALLBACK = "Simplicity Admin";

/** Throws during render to exercise Sentry.ErrorBoundary + source-map symbolication. */
function SentryTest(): never {
  throw new Error("Sentry test from web-admin SentryTest component");
}

/**
 * Polling state shared with every route that needs it. Defined here so the
 * module-level `ROUTES` array can reference per-state factories without
 * recreating its identity on every render — that stability lets the title
 * effect's deps stay narrow (just `location.pathname`).
 */
interface AppRoutesState {
  runsData: RunsReportResponse | null;
  runsLoading: boolean;
  runsError: string | null;
  runsSummaryData: RunsSummaryResponse | null;
  runsSummaryLoading: boolean;
  runsSummaryError: string | null;
  timelineData: TimelineRunsReportResponse | null;
  timelineLoading: boolean;
  timelineError: string | null;
}

/**
 * Single source of truth for every routable path in web-admin. Adding a route
 * without a `title` is a TypeScript compile error (WCAG 2.4.2 enforcement,
 * SIM-1398).
 */
interface RouteSpec {
  path: string;
  title: string;
  render: (state: AppRoutesState) => ReactNode;
}

const ROUTES: ReadonlyArray<RouteSpec> = [
  { path: "/login", title: "Sign in", render: () => <Login /> },
  {
    path: "/",
    title: "Overview",
    render: (s) => (
      <RunsReport
        data={s.runsSummaryData}
        loading={s.runsSummaryLoading}
        error={s.runsSummaryError}
      />
    ),
  },
  {
    path: "/timeline",
    title: "Timeline",
    render: (s) => (
      <Timeline data={s.timelineData} loading={s.timelineLoading} error={s.timelineError} />
    ),
  },
  {
    path: "/detail",
    title: "Runs",
    render: (s) => <RunsTable data={s.runsData} loading={s.runsLoading} error={s.runsError} />,
  },
  { path: "/config/projects", title: "Projects", render: () => <ProjectsList /> },
  { path: "/config/projects/new", title: "New project", render: () => <CreateProject /> },
  { path: "/config/projects/:id", title: "Project", render: () => <ProjectDetails /> },
  { path: "/config/connectors/new", title: "New connector", render: () => <CreateConnector /> },
  { path: "/config/connectors/:id", title: "Connector", render: () => <ConnectorDetails /> },
  {
    path: "/config/destinations/new",
    title: "New destination",
    render: () => <CreateDestination />,
  },
  { path: "/config/destinations/:id", title: "Destination", render: () => <DestinationDetails /> },
  {
    path: "/config/transform-definitions/new",
    title: "New transform",
    render: () => <CreateTransformDefinition />,
  },
  {
    path: "/config/transform-definitions/:id",
    title: "Transform",
    render: () => <TransformDefinitionDetails />,
  },
  {
    path: "/config/export-definitions/new",
    title: "New export",
    render: () => <CreateExportDefinition />,
  },
  {
    path: "/config/export-definitions/:id",
    title: "Export",
    render: () => <ExportDefinitionDetails />,
  },
  { path: "/config/feature-flags", title: "Feature flags", render: () => <FeatureFlagsList /> },
  {
    path: "/config/feature-flags/:key",
    title: "Feature flag",
    render: () => <FeatureFlagDetail />,
  },
  { path: "/config/api-keys/new", title: "New API key", render: () => <GenerateApiKey /> },
  { path: "/pipeline", title: "Pipeline", render: () => <PipelinePage /> },
  { path: "/pipeline/:projectId", title: "Pipeline", render: () => <PipelinePage /> },
  { path: "/dottie-context", title: "Dottie context", render: () => <DottieContextPage /> },
  { path: "/onsite", title: "Onsite", render: () => <OnsitePage /> },
  { path: "/debug/sentry", title: "Sentry test", render: () => <SentryTest /> },
];

const ROUTE_TITLES: ReadonlyArray<RouteTitle> = ROUTES.map((r) => ({
  path: r.path,
  title: r.title,
}));

function navLinkClass({ isActive }: { isActive: boolean }) {
  return `nav-link ${isActive ? "active" : ""}`;
}

/**
 * Top nav. Hidden on `/login` so unauthenticated users don't see the admin
 * section labels (SIM-1655). Every other route triggers a 401/403 → `/login`
 * redirect via `usePollRuns`, so when this paints the user is authenticated
 * (or about to be redirected).
 */
export function AppHeader() {
  const { pathname } = useLocation();
  if (pathname === "/login") return null;

  return (
    <header className="app-header max-md:flex-col max-md:gap-4 max-md:px-4">
      <h1 className="text-xl font-semibold text-foreground">Simplicity</h1>
      <nav aria-label="Main" className="flex gap-2">
        <NavLink to="/" end className={navLinkClass}>
          Overview
        </NavLink>
        <NavLink to="/detail" className={navLinkClass}>
          Detail
        </NavLink>
        <NavLink to="/timeline" className={navLinkClass}>
          Timeline
        </NavLink>
        <NavLink to="/pipeline" className={navLinkClass}>
          Pipeline
        </NavLink>
        <span className="mx-1 h-5 w-px self-center bg-border" />
        <NavLink to="/config/projects" className={navLinkClass}>
          Config
        </NavLink>
        <NavLink to="/config/feature-flags" className={navLinkClass}>
          Flags
        </NavLink>
        <NavLink to="/dottie-context" className={navLinkClass}>
          Dottie context
        </NavLink>
        <NavLink to="/onsite" className={navLinkClass}>
          Onsite
        </NavLink>
      </nav>
    </header>
  );
}

function usePollRuns<T>(path: string) {
  const navigate = useNavigate();
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const pollIntervalRef = useRef<number | null>(null);
  const inFlightRef = useRef(false);
  const hasLoadedRef = useRef(false);

  useEffect(() => {
    if (pollIntervalRef.current != null) return;

    async function fetchOnce() {
      if (window.location.pathname.includes("/login")) return;
      if (window.location.pathname.includes("/config/")) return;
      if (inFlightRef.current) return;
      inFlightRef.current = true;
      const isInitialLoad = !hasLoadedRef.current;
      if (isInitialLoad) setLoading(true);
      try {
        const apiBase = import.meta.env.VITE_API_URL ?? "";
        const response = await fetch(`${apiBase}${path}`, { credentials: "include" });
        if (response.status === 401 || response.status === 403) {
          void navigate("/login", { replace: true });
          return;
        }
        if (!response.ok) throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        const json = (await response.json()) as T;
        setData(json);
        setError(null);
        hasLoadedRef.current = true;
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to fetch data");
      } finally {
        if (isInitialLoad) setLoading(false);
        inFlightRef.current = false;
      }
    }

    void fetchOnce();
    pollIntervalRef.current = window.setInterval(() => void fetchOnce(), 10_000);
    return () => {
      if (pollIntervalRef.current != null) {
        window.clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [navigate, path]);

  return { data, loading, error };
}

function AppRoutes() {
  const {
    data: runsData,
    loading: runsLoading,
    error: runsError,
  } = usePollRuns<RunsReportResponse>("/api/reports/runs");

  const {
    data: runsSummaryData,
    loading: runsSummaryLoading,
    error: runsSummaryError,
  } = usePollRuns<RunsSummaryResponse>("/api/reports/runs/summary");

  const {
    data: timelineData,
    loading: timelineLoading,
    error: timelineError,
  } = usePollRuns<TimelineRunsReportResponse>("/api/reports/runs/timeline");

  const state: AppRoutesState = {
    runsData,
    runsLoading,
    runsError,
    runsSummaryData,
    runsSummaryLoading,
    runsSummaryError,
    timelineData,
    timelineLoading,
    timelineError,
  };

  return (
    <div className="flex min-h-screen flex-col">
      <DocumentTitleEffect
        routeTitles={ROUTE_TITLES}
        suffix={TITLE_SUFFIX}
        fallback={TITLE_FALLBACK}
      />
      <AppHeader />
      {/* SIM-1689 follow-up: flex-col so child pages can opt into flex-fill
          height (e.g. TransformDefinitionDetails). Pages that don't use
          flex-1 inside still stack top-aligned, matching prior behavior. */}
      <main className="flex flex-1 flex-col p-8 max-md:p-4">
        <SentryRoutes>
          {ROUTES.map((r) => (
            <Route key={r.path} path={r.path} element={r.render(state)} />
          ))}
        </SentryRoutes>
      </main>
    </div>
  );
}

const basename = window.location.pathname.startsWith("/web-admin") ? "/web-admin" : "/";

function App() {
  return (
    <DirtyFormProvider>
      <BrowserRouter basename={basename}>
        <ReleaseWatcher />
        <AppRoutes />
      </BrowserRouter>
    </DirtyFormProvider>
  );
}

export default App;
